import React, { useId } from 'react';
import { ConditionalWrapper } from '@emfood/shared';

import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

const StyledCheckbox = styled(Checkbox)(({ value }) => ({
  '&.MuiCheckbox-root': {
    color: value === undefined ? '#9e9e9e' : undefined,
    '&.Mui-checked': {
      color: 'primary', // Blue for true
    },
    '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': {
      color: '#gray', // Red for false
    },
  },
}));

const getNextTriState = (currentState: boolean | undefined) => {
  if (currentState === true) return false;
  if (currentState === false) return undefined;
  return true;
};

const getAriaChecked = (value: boolean | undefined) => {
  if (value === true) return 'true';
  if (value === false) return 'mixed';
  return 'false';
};

const getStateLabel = (state: boolean | undefined) => {
  if (state === true) return 'Included';
  if (state === false) return 'Excluded';
  return 'No preference';
};

interface TristateCheckboxProps {
  name: string;
  label: string;
  tooltip?: string;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
}

export const TriStateCheckbox = ({
  name,
  label,
  value,
  onChange,
  tooltip,
}: TristateCheckboxProps) => {
  const labelId = useId();

  return (
    <ConditionalWrapper
      condition={tooltip !== undefined}
      wrapper={(children) => <Tooltip title={tooltip}>{children}</Tooltip>}
    >
      <FormControlLabel
        sx={{
          mr: 0,
          ml: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiFormControlLabel-label': {
            width: '100%',
          },
        }}
        control={
          <StyledCheckbox
            name={name}
            value={value}
            checked={value === true || value === false}
            onChange={() => {
              const nextValue = getNextTriState(value);
              onChange(nextValue);
            }}
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={
              value === true ? <CheckBoxIcon /> : <IndeterminateCheckBoxIcon />
            }
            sx={{
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
              },
            }}
            inputProps={{
              tabIndex: 0,
              'aria-checked': getAriaChecked(value),
              'aria-labelledby': labelId,
            }}
          />
        }
        label={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Typography id={labelId}>{label}</Typography>
            <Box
              component="span"
              sx={{
                typography: 'caption',
                color:
                  // eslint-disable-next-line no-nested-ternary
                  value === true
                    ? 'primary.main'
                    : value === false
                      ? 'error.main'
                      : 'text.secondary',
                bgcolor: 'action.hover',
                width: '95px',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              {getStateLabel(value)}
            </Box>
          </Stack>
        }
      />
    </ConditionalWrapper>
  );
};
